import { CurrencyInput, DatePicker, TextareaInput } from 'components/FormControl';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';

import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { formatFileNameProduct } from 'utils/func';
import CustomFileInput from 'components/FormControl/CustomFileInput';
import {
  addExpenditurePeriodFromFundingSchema,
  addExpenditurePeriodSchema,
} from 'features/Project/validation';
import { yupResolver } from '@hookform/resolvers/yup';

import { PRODUCT_TYPE } from 'constant/summary-report.constant';

const AllocatedExpenditureDialog = forwardRef(
  ({ handleDownloadFile, productTypeShort, isExpenditureFromOtherFund }, ref) => {
    // #region Data
    const { t } = useTranslation();
    const toast = useRef(null);

    const [expenditureControl, setExpenditureControl] = useState();
    const [visible, setVisible] = useState(false);

    const {
      control,
      getValues,
      setValue,
      reset,
      trigger,
      formState: { errors, dirtyFields },
    } = useForm({
      mode: 'onChange',
      resolver: yupResolver(
        isExpenditureFromOtherFund
          ? addExpenditurePeriodFromFundingSchema
          : addExpenditurePeriodSchema
      ),
    });

    // #endregion Data

    // #region Event
    const showError = (message) => {
      toast.current.show({
        severity: 'error',
        summary: t('toast.error'),
        detail: message,
        life: 4000,
      });
    };

    useImperativeHandle(
      ref,
      () => ({
        open: (_expenditureControl) => {
          setExpenditureControl(_expenditureControl);
          setVisible(true);
          if (_expenditureControl.type === 'INSERT') {
            reset({});
          } else if (_expenditureControl.type === 'UPDATE') {
            reset(_expenditureControl.value);
            setValue('issueDate', new Date(_expenditureControl.value.issueDate));
          }
        },
      }),
      []
    );

    const handleAddExpenditure = async () => {
      const isValidTrigger = await trigger();
      if (!isValidTrigger) {
        showError(t('errorMessage.validationErrorMessage'));
        return;
      }

      const data = getValues();

      const { insert, update, index, type } = expenditureControl;

      if (isExpenditureFromOtherFund) {
        update({ ...data, isUpdateNewValue: true });
      } else if (type === 'INSERT' && insert) {
        insert(index, data);
      } else if (type === 'UPDATE' && update) {
        update(index, { ...data, isUpdateNewValue: true });
      }
      reset({});
      setVisible(false);
    };

    const formatHeader = () => {
      const type = expenditureControl?.type === 'INSERT' ? 'insert' : 'update';
      return isExpenditureFromOtherFund
        ? t(`submitDocument.expenditureDialog.${type}.titleFromOtherFund`)
        : t(`submitDocument.expenditureDialog.${type}.title`);
    };

    // #endregion Event

    return (
      <>
        <Dialog
          header={formatHeader}
          visible={visible}
          onHide={() => {
            setVisible(false);
          }}
          draggable={false}
          className="w-full sm:w-7 md:w-6 lg:w-5"
        >
          <div className="p-fluid">
            <div className="grid">
              <div className="col-6">
                <CurrencyInput
                  name="amount"
                  label={t('r05.a.a5.table.amount')}
                  isRequired
                  control={control}
                  errors={errors}
                />
              </div>
            </div>
            {!isExpenditureFromOtherFund && (
              <div className="grid">
                <div className="col-6">
                  <TextareaInput
                    name="decisionNo"
                    label={t('r05.a.a5.table.decisionNo')}
                    isRequired
                    control={control}
                    errors={errors}
                  />
                </div>
                <div className="col-6">
                  <DatePicker
                    name="issueDate"
                    label={t('r05.a.a5.table.issueDate')}
                    isRequired
                    control={control}
                    errors={errors}
                  />
                </div>
              </div>
            )}
            <div className="grid">
              <div className="col-12">
                <CustomFileInput
                  name="file"
                  label={t('submitDocument.product.evidenceFile')}
                  defaultNameFile={`${formatFileNameProduct(
                    'a.pdf',
                    expenditureControl?.projectCode,
                    productTypeShort,
                    typeof expenditureControl?.index !== 'number'
                      ? -1
                      : parseInt(expenditureControl?.index, 10) + 1
                  )}`}
                  isRequired
                  control={control}
                  errors={errors}
                  accept=".pdf,.doc,.docx"
                  maxFileSize={30000000}
                />
                {expenditureControl?.value?.filename ? (
                  <span
                    className="p-button p-button-text p-button-sm mt-1 p-0"
                    onClick={() => {
                      handleDownloadFile(
                        expenditureControl?.value?.filename,
                        formatFileNameProduct(
                          expenditureControl?.value?.filename || '',
                          expenditureControl?.projectCode,
                          productTypeShort,
                          !expenditureControl?.index
                            ? 1
                            : parseInt(expenditureControl?.index, 10) + 1
                        ),
                        'summary-report-allocated-expenditures'
                      );
                    }}
                  >
                    <i
                      className={`pi text-xl ${
                        expenditureControl?.value?.filename?.endsWith('.pdf')
                          ? 'pi-file-pdf text-red-500'
                          : 'pi-file-word'
                      }`}
                    />{' '}
                    <span className="text-blue-500 underline font-italic ml-1">
                      {formatFileNameProduct(
                        expenditureControl?.value?.filename || '',
                        expenditureControl?.projectCode,
                        productTypeShort,
                        !expenditureControl?.index ? 1 : parseInt(expenditureControl?.index, 10) + 1
                      )}
                    </span>
                  </span>
                ) : (
                  <span>
                    {expenditureControl?.value?.file?.name &&
                      formatFileNameProduct(
                        expenditureControl?.value?.file?.name || '',
                        expenditureControl?.projectCode,
                        productTypeShort,
                        !expenditureControl?.index ? 1 : parseInt(expenditureControl?.index, 10) + 1
                      )}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-content-end mt-4">
            <Button
              label={
                expenditureControl?.type === 'INSERT'
                  ? t('formLayout.action.add')
                  : t('formLayout.action.update')
              }
              type="submit"
              severity="info"
              onClick={handleAddExpenditure}
              className="w-8rem"
              disabled={!Object.keys(dirtyFields)?.length}
            />
          </div>
        </Dialog>
        <Toast ref={toast} />
      </>
    );
  }
);

AllocatedExpenditureDialog.propTypes = {
  handleDownloadFile: PropTypes.func.isRequired,
  productTypeShort: PropTypes.string,
  isExpenditureFromOtherFund: PropTypes.bool,
};

AllocatedExpenditureDialog.defaultProps = {
  productTypeShort: PRODUCT_TYPE.SOFT_PRODUCT.short,
  isExpenditureFromOtherFund: false,
};

export default AllocatedExpenditureDialog;
